import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from '@chakra-ui/react';
import { useFormContext, useController } from 'react-hook-form';
import { useIMask } from 'react-imask';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { sizes } from '../../theme';
import useSchema from '../../hooks/UseSchema';
import { useConfig } from '../../contexts/ConfigProvider';
import { EVENTS_NAME } from '../../modules/events';
import { eventPayload } from '../../modules/utils';

const removeLastPassFrame = () => {
  const lastpass = document.querySelector('[data-lastpass-root]');
  if (lastpass) {
    lastpass.remove();
  }
};

const propTypes = {
  autoComplete: PropTypes.string,
  icon: PropTypes.object,
  label: PropTypes.string,
  mask: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  rules: PropTypes.object,
  onChange: PropTypes.func,
  allowOnlyNumbers: PropTypes.bool,
};

const defaultProps = {
  autoComplete: undefined,
  icon: undefined,
  label: undefined,
  mask: undefined,
  placeholder: undefined,
  width: '100%',
  rules: {},
  onChange: undefined,
  allowOnlyNumbers: false,
};

const FormInput = ({
  autoComplete,
  icon,
  label,
  mask,
  name,
  placeholder,
  rules,
  width,
  onChange,
  allowOnlyNumbers,
  ...rest
}) => {
  const { formState: { errors, control }, getValues } = useFormContext();
  const [inputPlaceholder, updateInputPlaceholder] = useState(' ');
  const [lastValues, setLastValues] = useState({});
  const { getIsRequired } = useSchema();
  const { field } = useController({ name, control, rules });
  const { ref } = useIMask({ mask, unmask: true }, { onAccept: (value) => field.onChange(value) });

  const { triggerEvent } = useConfig();

  useEffect(() => {
    removeLastPassFrame();
    document.addEventListener('scrollend', removeLastPassFrame);
    return () => {
      document.removeEventListener('scrollend', removeLastPassFrame);
    };
  }, []);

  useEffect(() => {
    setLastValues(cloneDeep(getValues()));
  }, [control]);

  const handleBlur = (e) => {
    updateInputPlaceholder(' ');
    field.onBlur(e);
    const fieldName = e.target.name;
    let fieldValue = field.value;
    if (!rules.spaceAllowed) {
      fieldValue = fieldValue.trim();
      field.onChange(fieldValue);
    }
    triggerEvent(
      EVENTS_NAME.FORM_FIELD_CHANGE,
      eventPayload(fieldName, lastValues, fieldValue),
    );
    setLastValues((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
  };

  return (
    <FormControl
      isInvalid={get(errors, name)}
      marginBottom={sizes._12px}
      variant="floating"
      width={width}
      isRequired={getIsRequired(name)}
    >
      <InputGroup>
        {icon ? <InputLeftElement pointerEvents="none" children={icon} /> : <div />}
        <Input
          {...rest}
          {...field}
          autoComplete={autoComplete || 'always-off'}
          id={name}
          inputMode={allowOnlyNumbers ? 'numeric' : undefined}
          pattern={allowOnlyNumbers ? '[0-9]*' : undefined}
          onChange={(e) => {
            field.onChange(e);
            if (onChange) { onChange(e); }
          }}
          onKeyDown={(e) => {
            if (allowOnlyNumbers) {
              const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete',
                'Control',
                'Meta', // for macOS Command key

              ];

              // Allow key combos like Ctrl+V or Cmd+V
              if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'v')) {
                return;
              }

              // Block other non-numeric keys
              if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }
          }}
          onPaste={(e) => {
            if (allowOnlyNumbers) {
              const pasted = e.clipboardData.getData('text');
              if (!/^\d+$/.test(pasted)) {
                e.preventDefault(); // Block if pasted text is not all digits
              }
            }
          }}
          onDrop={(e) => {
            if (allowOnlyNumbers) {
              const dropped = e.dataTransfer.getData('text');
              if (!/^\d+$/.test(dropped)) {
                e.preventDefault(); // Block drag & drop non-numeric text
              }
            }
          }}
          onBlur={handleBlur}
          onFocus={() => updateInputPlaceholder(placeholder || inputPlaceholder)}
          placeholder={inputPlaceholder}
          ref={ref}
        />
        <FormLabel onClick={() => ref.current.focus()}>
          <Text variant="md">
            {label}
            {rules?.required && (
              <Text as="span" color="red" marginLeft="4px">
                *
              </Text>
            )}
          </Text>
        </FormLabel>
      </InputGroup>
      <FormErrorMessage>
        <Text variant="sm" marginTop={sizes._4px} color="red.accent">
          {get(errors, name)?.message}
        </Text>
      </FormErrorMessage>
    </FormControl>
  );
};

FormInput.propTypes = propTypes;
FormInput.defaultProps = defaultProps;

export default FormInput;
